import * as React from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { cn } from "@/lib/cn";
import { isAndroid } from "@/platform";

// Minimum bottom padding to reserve for the Android system navigation bar.
// At targetSdk 35+, Android forces edge-to-edge — the WebView draws under
// the nav bar. Capacitor 8 doesn't automatically propagate WindowInsets to
// the WebView's env(safe-area-inset-bottom), so the CSS env() returns 0
// even though the bar is physically present. A hard fallback is the only
// pure-JS/CSS way to guarantee clearance without the @capacitor-community/
// safe-area plugin (which would require a new APK release).
//
// 48px covers the 3-button nav bar (48dp on virtually all Android devices)
// and the gesture nav bar (24dp, well under 48px). On iOS WKWebView,
// env(safe-area-inset-bottom) reliably returns ~34px for the home indicator,
// so iOS gets a smaller fallback for an unobtrusive visual buffer.
const ANDROID_NAV_BAR_FALLBACK_PX = 48;
const IOS_FALLBACK_PX = 8;

export interface MobileBottomSheetProps {
  /** Controls open/close. Sheet stays mounted so CSS transitions animate. */
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}

/**
 * Animated slide-up portal for mobile data-entry flows.
 *
 * Stays mounted in the DOM so the CSS `translate-y` / `opacity` transitions
 * animate both enter and exit. Body scroll is locked while open.
 * Rendered into `document.body` via `createPortal` — always sits above
 * all page content regardless of stacking contexts.
 *
 * Usage:
 *   <MobileBottomSheet open={open} onClose={() => setOpen(false)} title="Add period">
 *     <MyForm />
 *   </MobileBottomSheet>
 */
export function MobileBottomSheet({
  open,
  onClose,
  title,
  children,
}: MobileBottomSheetProps): React.ReactElement {
  // Lock body scroll while sheet is visible.
  React.useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Pick the bottom-padding fallback based on platform. isAndroid() reads
  // Capacitor.getPlatform() which is fixed for the session, so calling once
  // at render is fine — no need to memoize.
  const fallbackPx = isAndroid() ? ANDROID_NAV_BAR_FALLBACK_PX : IOS_FALLBACK_PX;
  const safeBottom = `max(env(safe-area-inset-bottom), ${fallbackPx}px)`;

  return createPortal(
    <div
      className={cn(
        "fixed inset-0 z-[1100] flex flex-col justify-end transition-[visibility] duration-300",
        open ? "visible" : "invisible",
      )}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      {/* Backdrop — fades in/out */}
      <div
        className={cn(
          "absolute inset-0 bg-black/50 transition-opacity duration-300",
          open ? "opacity-100" : "opacity-0",
        )}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Sheet panel — slides up/down */}
      <div
        className={cn(
          "relative flex w-full max-h-[92dvh] flex-col overflow-hidden rounded-t-2xl bg-[color:var(--gray-0)] shadow-2xl transition-transform duration-300",
          open ? "translate-y-0" : "translate-y-full",
        )}
      >
        {/* Drag handle */}
        <div className="flex justify-center pb-2 pt-3">
          <div className="h-1 w-10 rounded-full bg-[color:var(--gray-300)]" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between border-b border-[color:var(--gray-200)] px-4 pb-3">
          <h3 className="text-base font-semibold text-gray-900">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1.5 text-gray-500 hover:bg-[color:var(--gray-100)]"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        {/* Scrollable content area. paddingBottom combines:
            - 16px visual breathing room above the safe area
            - platform-aware safe-area inset (see fallback rationale at top
              of file) to keep content above Android nav bars / iOS home bar */}
        <div
          className="overflow-x-hidden overflow-y-auto px-4 pt-4"
          style={{ paddingBottom: `calc(16px + ${safeBottom})` }}
        >
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );
}
